<template>
  <page-view
    title="Translations"
    icon="text-24"
    class="subpage"
    backLink="/appearance"
    backText="Back to Appearance"
  >
    <template v-slot:tabs>
      <page-tab title="Settings" :active="activeTab === ''" link="/appearance/translations" @click.native="handleLink($event, '/appearance/translations')" icon="settings-gear-24" />
      <page-tab title="Chat/Q&A" :active="activeTab === 'chat-qa'" link="/appearance/translations/chat-qa" @click.native="handleLink($event, '/appearance/translations/chat-qa')" icon="chat-24" />
      <page-tab title="Giveaway" :active="activeTab === 'giveaway'" link="/appearance/translations/giveaway" @click.native="handleLink($event, '/appearance/translations/giveaway')" icon="gift-24" />
      <page-tab title="Forms" :active="activeTab === 'form'" link="/appearance/translations/form" @click.native="handleLink($event, '/appearance/translations/form')" icon="edit-pen2" />
      <page-tab title="Products" :active="activeTab === 'products'" link="/appearance/translations/products" @click.native="handleLink($event, '/appearance/translations/products')" icon="tshirt-product-24" />
      <page-tab title="Poster" :active="activeTab === 'poster'" link="/appearance/translations/poster" @click.native="handleLink($event, '/appearance/translations/poster')" icon="video" />
      <page-tab title="Common" :active="activeTab === 'common'" link="/appearance/translations/common" @click.native="handleLink($event, '/appearance/translations/common')" icon="eye-24" />
    </template>
    <template v-if="activeTab === ''">
      <card-minimal title="General Settings">
        <div class="d-f f-g fd-c fd-md-r p-a-0 p-a-md-4 general-settings">
          <div class="p-a-4 d-f ai-c jc-c">
            <img src="@/assets/translations.png" class="m-a" />
          </div>
          <div class="p-a-4 f-2 jc-c d-f fd-c">
            <h2>Setting up text in player</h2>
            <p>The live shopping player shows many different text elements. These can be customised to match your brand and translated into another language. Choose the type of text to customise in the menu above</p>
          </div>
        </div>
      </card-minimal>
    </template>
    <template v-else>
      <card-minimal>
        <translation v-for="translation in filteredTranslations" :id="translation.key" :data="translation" :key="translation.key" @change="setTranslation" />
      </card-minimal>
    </template>
    <template v-slot:floatingBottom v-if="updateable">
      <div class="d-f ai-c jc-c bottom">
        <p class="m-r-4">You have made changes to this page</p>
        <Button class="ml-a no-wrap" @click="update" :disabled="state !== ''" :loading="state === 'saving'">Save changes</Button>
      </div>
    </template>
  </page-view>
</template>

<!-- eslint-disable object-curly-newline -->
<script>
import Vue from 'vue';
import Translation from '@/components/Appearance/Translation.vue';

export default {
  components: {
    Translation,
  },
  computed: {
    /**
     * Return the current config in store
     * @returns {Object}
     */
    lvspConfig() {
      return this.$store.getters['Play/lvspConfig'];
    },
    /**
     * Return the current active tab
     * @returns {String}
     */
    activeTab() {
      const tab = this.$route.path.split('/').pop();
      return tab === 'translations' ? '' : tab;
    },
    /**
     * Check if any of the translationstrings are updateable
     * @returns {Boolean}
     */
    updateable() {
      const translations = Object.entries(this.translations);
      let updateable = false;
      translations.forEach(([, value]) => {
        if (value.value !== value.origValue) {
          updateable = true;
        }
      });

      return updateable;
    },
    /**
     * Return a filtered list of our translationkeys based on active tab
     * @returns {Array}
     */
    filteredTranslations() {
      const {
        activeTab,
      } = this;

      const translations = [];
      Object.entries(this.translations).forEach(([key, value]) => {
        if (value.section === activeTab) {
          translations.push({
            ...value,
            key,
          });
        }
      });

      return translations;
    },
  },
  methods: {
    /**
     * Set translation value in our little list
     * @param {Object} obj
     * @param {String} obj.key
     * @param {String} obj.value
     * @returns {void}
     */
    setTranslation({ key, value }) {
      Vue.set(this.translations[key], 'value', value);
    },
    /**
     * Update translations keys
     * @returns {void}
     */
    async update() {
      const translations = {};
      Object.entries(this.translations).forEach(([key, value]) => {
        translations[key] = value.value;
      });

      this.state = 'saving';

      const result = await this.$store.dispatch('Play/updateLVSPConfig', { strings: { en: translations } });

      this.state = '';

      if (result.error) {
        swal({ title: this.$t('errors.error_occured'), text: this.$t('settings.liveshopping.could_not_save'), icon: 'error' });
        return;
      }

      Object.entries(translations).forEach(([key, value]) => {
        Vue.set(this.translations[key], 'origValue', value);
      });
    },
  },
  data() {
    return {
      baseLang: 'en',
      state: '',
      translations: {
        // CHAT
        chat_title: { section: 'chat-qa', string: 'Chat title', description: 'The title visible above the chat sidebar on desktop devices', placeholder: 'Chat', value: '', origValue: '' },
        qa_title: { section: 'chat-qa', string: 'Q&A title', description: 'The title visible aboce the qa sidebar on desktop devices', placeholder: 'QA', value: '', origValue: '' },
        set_nickname_to_chat: { section: 'chat-qa', string: 'Set nickname to chat', description: 'Replace text in Set nickname to chat button', placeholder: 'Set nickname to chat', value: '', origValue: '' },
        set_nickname_to_chat_placeholder: { section: 'chat-qa', string: 'Choose a nickname', description: 'Replace text for Choose a nickname placeholder', placeholder: 'Choose a nickname', value: '', origValue: '' },
        set_nickname_to_qa: { section: 'chat-qa', string: 'Set a nickname to ask questions', description: 'Replace text in Set a nickname to ask questions button', placeholder: 'Set a nickname to ask questions', value: '', origValue: '' },
        in_reply_to: { section: 'chat-qa', string: 'In reply to', description: 'Replace text when replying to messages', placeholder: 'In reply to', value: '', origValue: '' },
        chat_hidden: { section: 'chat-qa', string: 'Chat hidden', description: 'Replace text in tooltip when chat is hidden', placeholder: 'Chat hidden', value: '', origValue: '' },
        chat_visible: { section: 'chat-qa', string: 'Chat visible', description: 'Replace text in tooltip when chat is hidden', placeholder: 'Chat visible', value: '', origValue: '' },
        qa_hidden: { section: 'chat-qa', string: 'Q&A hidden', description: 'Replace text in tooltip when QA is hidden', placeholder: 'QA hidden', value: '', origValue: '' },
        qa_visible: { section: 'chat-qa', string: 'Q&A visible', description: 'Replace text in tooltip when QA is visible', placeholder: 'QA visible', value: '', origValue: '' },
        chat_as: { section: 'chat-qa', string: 'Chat as', description: '"Replace placeholder text in chat input', placeholder: 'Chat as', value: '', origValue: '' },
        qa_as: { section: 'chat-qa', string: 'Q&A as', description: '"Replace placeholder text in question input', placeholder: 'Ask a question as', value: '', origValue: '' },
        question_from: { section: 'chat-qa', string: 'Question from', description: 'Replace text under question from which user sent it in', placeholder: 'Question from', value: '', origValue: '' },
        change_nickname: { section: 'chat-qa', string: 'Change nickname', description: 'Replace text for change nickname option', placeholder: 'Change nickname', value: '', origValue: '' },
        chat_will_be_available: { section: 'chat-qa', string: 'Chat will be available', description: 'Replace text for first part of "chat will be available X min before broadcast begins"', placeholder: 'The chat will be available', value: '', origValue: '' },
        qa_will_be_available: { section: 'chat-qa', string: 'Q&A will be available', description: 'Replace text for first part of "qa will be available X min before broadcast begins', placeholder: 'The qa will be available', value: '', origValue: '' },
        min_before_broadcast_begins: { section: 'chat-qa', string: 'Min before broadcast begins', description: 'Replace text for second part of "chat will be available X min before broadcast begins"', placeholder: 'min before broadcast begins', value: '', origValue: '' },
        // GIVEAWAY
        giveaway: { section: 'giveaway', string: 'Giveaway', description: 'Replace text "Giveaway" when displaying giveaway title', placeholder: 'Giveaway', value: '', origValue: '' },
        entered_this_giveaway: { section: 'giveaway', string: 'You have entered this giveaway.', description: 'Replace information text that you have entered this giveaway', placeholder: 'You have entered this giveaway', value: '', origValue: '' },
        enter_email_address: { section: 'giveaway', string: 'Enter your email address', description: 'Replace text in the email input', placeholder: 'Enter your email address', value: '', origValue: '' },
        confirm_email_address: { section: 'giveaway', string: 'Confirm your email address', description: 'Replace text in the confirm email input', placeholder: 'Confirm your email address', value: '', origValue: '' },
        enter_our_giveaway: { section: 'giveaway', string: 'Enter our giveaway', description: 'Replace text for the enter our giveaway button', placeholder: 'Enter our giveaway', value: '', origValue: '' },
        not: { section: 'giveaway', string: 'Not', description: 'Replace text "not" when displaying text if user has already supplied details and might want to change.', placeholder: 'not', value: '', origValue: '' },
        click_here: { section: 'giveaway', string: 'Click here', description: 'Replace text "click_here" when displaying text if user has already supplied details and might want to change.', placeholder: 'Click here', value: '', origValue: '' },
        to_change_your_details: { section: 'giveaway', string: 'Change your details', description: 'Replace text "to change your details." when displaying text if user has already supplied details and might want to change.', placeholder: 'to change your details.', value: '', origValue: '' },
        giveaway_is_over_and: { section: 'giveaway', string: '[name] giveaway is over', description: 'Replace text "giveaway is over and" when displaying text that the giveaway is over and all winner(s) have been drawn.', placeholder: 'giveaway is over and', value: '', origValue: '' },
        all_winners: { section: 'giveaway', string: 'All winners', description: 'Replace text "all winners have" when displaying text that the giveaway is over and all winners have been drawn.', placeholder: 'all winners have', value: '', origValue: '' },
        the_winner: { section: 'giveaway', string: 'The winner', description: 'Replace text "the winner has" when displaying text that the giveaway is over and all winners have been drawn.', placeholder: 'the winner has', value: '', origValue: '' },
        been_drawn: { section: 'giveaway', string: 'Been drawn', description: 'Replace text "been drawn" when displaying text that the giveaway is over and all winners have been drawn.', placeholder: 'been drawn', value: '', origValue: '' },
        winner: { section: 'giveaway', string: 'Winner', description: 'Replace text "Winner" when displaying single winner.', placeholder: 'Winner', value: '', origValue: '' },
        winners: { section: 'giveaway', string: 'Winners', description: 'Replace text "Winners" when displaying multiple winners.', placeholder: 'Winners', value: '', origValue: '' },
        giveaway_is_over: { section: 'giveaway', string: 'Giveaway is over', description: 'Replace title text for when giveaway is over', placeholder: 'Giveaway is over', value: '', origValue: '' },
        drawing_winner: { section: 'giveaway', string: 'Drawing winner', description: 'Replace title text for when drawing a single winner', placeholder: 'Drawing winner', value: '', origValue: '' },
        drawing_winners: { section: 'giveaway', string: 'Drawing winners', description: 'Replace title text for when drawing multiple winners', placeholder: 'Drawing winners', value: '', origValue: '' },
        giveaway_has_started: { section: 'giveaway', string: 'Giveaway has started', description: 'Replace text "has started!" when displaying message in chat that a giveaway has started', placeholder: 'has started!', value: '', origValue: '' },
        entry_for: { section: 'giveaway', string: 'Entry for', description: 'Replace text "Entry for" when displaying message in chat that giveaway has closed', placeholder: 'Entry for', value: '', origValue: '' },
        giveaway_has_closed: { section: 'giveaway', string: 'Giveaway has closed', description: 'Replace text "has closed!" when displaying message in chat that a giveaway has closed', placeholder: 'has closed!', value: '', origValue: '' },
        winners_for_giveaway: { section: 'giveaway', string: 'Winners for giveaway', description: 'Replace text "winners for giveaway" when displaying message in chat that a giveaway has had its winners drawn', placeholder: 'winners for giveaway', value: '', origValue: '' },
        has_been_drawn_congrats: { section: 'giveaway', string: 'Congrats winner(s) ', description: 'Replace text "has been drawn, congrats to all winner(s)" when displaying message in chat that a giveaway has had its winners drawn', placeholder: 'has been drawn, congrats to all winner(s)', value: '', origValue: '' },
        // PRODUCTS
        products_title: { section: 'products', string: 'Products title', description: 'The title visible above the products sidebar on desktop devices', placeholder: 'Products', value: '', origValue: '' },
        read_more: { section: 'products', string: 'Read more', description: 'Replace "read more" text when product information is too long', placeholder: 'Read more', value: '', origValue: '' },
        read_less: { section: 'products', string: 'Read less', description: 'Replace "read less" text that is shown after "read more" has been clicked', placeholder: 'Read less', value: '', origValue: '' },
        checkout_button: { section: 'products', string: 'Checkout', description: 'Replace text inside checkout button', placeholder: 'Go to checkout', value: '', origValue: '' },
        checkout_button_notice: { section: 'products', string: 'Checkout button notice', description: 'Text beneath checkout button if you dont have any handlers for quantity change/remove product', placeholder: '', value: '', origValue: '' },
        add_to_cart: { section: 'products', string: 'Add to cart', description: 'Replace text inside add to cart button', placeholder: 'Add to cart', value: '', origValue: '' },
        added_to_cart: { section: 'products', string: 'Added to cart', description: 'Replace text inside add to cart button after product has been added', placeholder: 'Added', value: '', origValue: '' },
        'out-of-stock': { section: 'products', string: 'Product is out of stock', description: 'Replace message when product is out of stock', placeholder: 'Product is out of stock', value: '', origValue: '' },
        go_to_product: { section: 'products', string: 'Go to product', description: 'Replace text inside Go to product if the player is on another page than your webshop', placeholder: 'Go to product', value: '', origValue: '' },
        show_in_video: { section: 'products', string: 'Show in video', description: 'Replace text inside Show in video button', placeholder: 'Show in video', value: '', origValue: '' },
        shopping_cart: { section: 'products', string: 'Shopping cart', description: 'Replace text inside Shopping cart button', placeholder: 'Shopping cart', value: '', origValue: '' },
        hide_cart: { section: 'products', string: 'Hide cart', description: 'Replace text inside Hide cart button', placeholder: 'Hide cart', value: '', origValue: '' },
        color: { section: 'products', string: 'Color', description: 'Replace text for Color attribute', placeholder: 'Color', value: '', origValue: '' },
        size: { section: 'products', string: 'Size', description: 'Replace text for Size attribute', placeholder: 'Size', value: '', origValue: '' },
        choose_placeholder: { section: 'products', string: 'Choose', description: 'Replace placeholder text in the products dropdown menu', placeholder: 'Choose', value: '', origValue: '' },
        // POSTER
        upcoming: { section: 'poster', string: 'Upcoming', description: 'Replace text for "upcoming" status', placeholder: 'upcoming', value: '', origValue: '' },
        recorded_today: { section: 'poster', string: 'Recorded today', description: 'Replace text for "recorded today" status', placeholder: 'recorded today', value: '', origValue: '' },
        cancelled: { section: 'poster', string: 'Cancelled', description: 'Replace text for "cancelled" status', placeholder: 'cancelled', value: '', origValue: '' },
        year_ago: { section: 'poster', string: 'Year ago', description: 'Replace "year ago" text on poster', placeholder: 'year ago', value: '', origValue: '' },
        years_ago: { section: 'poster', string: 'Years ago', description: 'Replace "years ago" text on poster', placeholder: 'years ago', value: '', origValue: '' },
        month_ago: { section: 'poster', string: 'Month ago', description: 'Replace "month ago" text on poster', placeholder: 'month ago', value: '', origValue: '' },
        months_ago: { section: 'poster', string: 'Months ago', description: 'Replace "months ago" text on poster', placeholder: 'months ago', value: '', origValue: '' },
        day_ago: { section: 'poster', string: 'Day ago', description: 'Replace "day ago" text on poster', placeholder: 'day ago', value: '', origValue: '' },
        days_ago: { section: 'poster', string: 'Days ago', description: 'Replace "days ago" text on poster', placeholder: 'days ago', value: '', origValue: '' },
        // PLAYER
        turn_on_sound: { section: 'common', string: 'Turn on sound', description: 'Replace text for turning on sound', placeholder: 'Press here to turn on the sound', value: '', origValue: '' },
        close: { section: 'common', string: 'Close', description: 'Replace text for close buttons', placeholder: 'Close', value: '', origValue: '' },
        required: { section: 'common', string: 'Required', description: 'Replace required text', placeholder: 'Required', value: '', origValue: '' },
        i_agree: { section: 'common', string: 'I agree to', description: 'Replace first part of I agree to the terms and conditions', placeholder: 'I agree to the', value: '', origValue: '' },
        terms_and_conditions: { section: 'common', string: 'Required', description: 'Replace second part of I agree to the terms and conditions', placeholder: 'terms and conditions', value: '', origValue: '' },
        choose_common_placeholder: { section: 'common', string: 'Choose', description: 'Replace placeholder text in the dropdown menu', placeholder: 'Choose', value: '', origValue: '' },
        tomorrow: { section: 'common', string: 'Tomorrow', description: 'Replace tomorrow text in countdown', placeholder: 'Tomorrow', value: '', origValue: '' },
        // FORM { section: '', string: '', description: '', placeholder: '', value: '', origValue: '' },
        // form_send:               The details you provided has been submitted! You can now close the modal.
        // form_already_filled     You have already filled out this form.
        // form_completed          This form has been completed and no more entries are possible.
        // form_send               Send in details
        // form_error              There was an error while submitting the form. Please try again.
        form_sent: { section: 'form', string: 'Form submitted', description: 'Change the text that appears after a form has been submitten.', placeholder: 'Thanks for your submission!', value: '', origValue: '' },
        form_already_filled: { section: 'form', string: 'Form already filled', description: 'Change the text that appears if accessing a form you\'ve already submitted.', placeholder: 'You have already filled out this form.', value: '', origValue: '' },
        form_completed: { section: 'form', string: 'Form completed', description: 'Change the text that appears of the form has been completed and no longed accepts any more submissions.', placeholder: 'This form has been completed and no more entries are possible.', value: '', origValue: '' },
        form_send: { section: 'form', string: 'Form send', description: 'Change the text on the button when submitting a form', placeholder: 'Send in details', value: '', origValue: '' },
        form_error: { section: 'form', string: 'Form error', description: 'Change the error text when a form fails.', placeholder: 'There was an error while submitting the form. Please try again.', value: '', origValue: '' },
        // form_title: { section: 'form', string: 'Form', description: 'Change the title when viewing a form', placeholder: 'Form', value: '', origValue: '' },
        available_forms: { section: 'form', string: 'Available forms', description: 'Change the title when listing multuple forms', placeholder: 'Available forms', value: '', origValue: '' },
      },
    };
  },
  async mounted() {
    await this.$store.dispatch('Play/getLVSPConfig');
    const {
      strings = { en: {} },
    } = this.lvspConfig;

    const translations = Object.entries(strings.en);
    translations.forEach(([key, value]) => {
      if (typeof this.translations[key] !== 'undefined') {
        this.translations[key] = {
          ...this.translations[key],
          value,
          origValue: value,
        };
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.general-settings {
  h2 {
    font-size: 24px;
    color: #000000;
    line-height: 32px;
    font-weight: 500;
    margin-bottom: 18px;
  }

  p {
    color: #52586B;
    font-size: 16px;
    margin-bottom: 8px;
    line-height: 24px;
  }
}

.bottom {
  p {
    font-style: italic;
  }
}
</style>
